import React, { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { nestApi } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { toast } from "sonner";
import { SearchableSelect } from "@/components/ui/searchable-select";

// ---------------------------------------------------------------------------
// BANK FIELD SCHEMAS
// Each schema is { fields: Array<FieldDef> }
// FieldDef: { key, label, placeholder?, required?, type?: 'text'|'select', options?: string[] }
// Common field "account_holder_name" is always shown separately.
// ---------------------------------------------------------------------------

const SCHEMAS = {
  // ── UK ──────────────────────────────────────────────────────────────────
  GBR: {
    fields: [
      { key: "sort_code",      label: "Sort Code",      placeholder: "12-34-56", required: true },
      { key: "account_number", label: "Account Number", placeholder: "12345678 (8 digits)", required: true },
    ],
  },

  // ── United States ────────────────────────────────────────────────────────
  USA: {
    fields: [
      { key: "routing_number", label: "Routing Number (ABA)", placeholder: "9 digits", required: true },
      { key: "account_number", label: "Account Number", required: true },
      { key: "account_type",   label: "Account Type", type: "select", options: ["Checking", "Savings"], required: true },
    ],
  },

  // ── Australia ────────────────────────────────────────────────────────────
  AUS: {
    fields: [
      { key: "bsb",            label: "BSB Number", placeholder: "XXX-XXX", required: true },
      { key: "account_number", label: "Account Number", required: true },
    ],
  },

  // ── Canada ───────────────────────────────────────────────────────────────
  CAN: {
    fields: [
      { key: "transit_number",     label: "Transit Number",     placeholder: "5 digits", required: true },
      { key: "institution_number", label: "Institution Number", placeholder: "3 digits", required: true },
      { key: "account_number",     label: "Account Number", required: true },
    ],
  },

  // ── India ────────────────────────────────────────────────────────────────
  IND: {
    fields: [
      { key: "ifsc",           label: "IFSC Code",      placeholder: "AAAA0000000", required: true },
      { key: "account_number", label: "Account Number", required: true },
      { key: "account_type",   label: "Account Type", type: "select", options: ["Savings", "Current", "Salary"], required: true },
      { key: "bank_name",      label: "Bank Name", required: true },
    ],
  },

  // ── Mexico ───────────────────────────────────────────────────────────────
  MEX: {
    fields: [
      { key: "clabe", label: "CLABE", placeholder: "18 digits", required: true },
      { key: "bank_name", label: "Bank Name", required: true },
    ],
  },

  // ── Brazil ───────────────────────────────────────────────────────────────
  BRA: {
    fields: [
      { key: "bank_code",      label: "Bank Code (Código do Banco)", placeholder: "3 digits", required: true },
      { key: "agency",         label: "Agency (Agência)", required: true },
      { key: "account_number", label: "Account Number (Conta)", required: true },
      { key: "cpf",            label: "CPF / CNPJ", required: true },
    ],
  },

  // ── Japan ────────────────────────────────────────────────────────────────
  JPN: {
    fields: [
      { key: "bank_code",      label: "Bank Code (金融機関コード)", placeholder: "4 digits", required: true },
      { key: "branch_code",    label: "Branch Code (支店コード)",   placeholder: "3 digits", required: true },
      { key: "account_number", label: "Account Number (口座番号)",  required: true },
      { key: "account_type",   label: "Account Type", type: "select", options: ["Ordinary (普通)", "Current (当座)"], required: true },
    ],
  },

  // ── China ────────────────────────────────────────────────────────────────
  CHN: {
    fields: [
      { key: "account_number", label: "Bank Account Number (银行卡号)", required: true },
      { key: "bank_name",      label: "Bank Name (银行名称)", required: true },
      { key: "branch_name",    label: "Branch Name (支行名称)", required: true },
    ],
  },

  // ── New Zealand ──────────────────────────────────────────────────────────
  NZL: {
    fields: [
      { key: "account_number", label: "Account Number", placeholder: "XX-XXXX-XXXXXXX-XX", required: true },
    ],
  },

  // ── Singapore ────────────────────────────────────────────────────────────
  SGP: {
    fields: [
      { key: "bank_code",      label: "Bank Code",   placeholder: "4 digits", required: true },
      { key: "branch_code",    label: "Branch Code", placeholder: "3 digits", required: true },
      { key: "account_number", label: "Account Number", required: true },
    ],
  },

  // ── Hong Kong ────────────────────────────────────────────────────────────
  HKG: {
    fields: [
      { key: "bank_code",      label: "Bank Code",   placeholder: "3 digits", required: true },
      { key: "branch_code",    label: "Branch Code", placeholder: "3 digits", required: true },
      { key: "account_number", label: "Account Number", required: true },
    ],
  },

  // ── South Africa ─────────────────────────────────────────────────────────
  ZAF: {
    fields: [
      { key: "account_number", label: "Account Number", required: true },
      { key: "branch_code",    label: "Universal Branch Code", placeholder: "6 digits", required: true },
      { key: "bank_name",      label: "Bank Name", required: true },
      { key: "account_type",   label: "Account Type", type: "select", options: ["Cheque/Current", "Savings", "Transmission"], required: true },
    ],
  },

  // ── Nigeria ──────────────────────────────────────────────────────────────
  NGA: {
    fields: [
      { key: "account_number", label: "Account Number (NUBAN)", placeholder: "10 digits", required: true },
      { key: "bank_name",      label: "Bank Name", required: true },
    ],
  },

  // ── Bangladesh ───────────────────────────────────────────────────────────
  BGD: {
    fields: [
      { key: "routing_number", label: "Routing Number", placeholder: "9 digits", required: true },
      { key: "account_number", label: "Account Number", required: true },
      { key: "bank_name",      label: "Bank Name", required: true },
    ],
  },

  // ── Philippines ──────────────────────────────────────────────────────────
  PHL: {
    fields: [
      { key: "account_number", label: "Account Number", required: true },
      { key: "bank_name",      label: "Bank Name", required: true },
    ],
  },

  // ── Indonesia ────────────────────────────────────────────────────────────
  IDN: {
    fields: [
      { key: "account_number", label: "Account Number", required: true },
      { key: "bank_name",      label: "Bank Name", required: true },
    ],
  },

  // ── Vietnam ──────────────────────────────────────────────────────────────
  VNM: {
    fields: [
      { key: "account_number", label: "Account Number", required: true },
      { key: "bank_name",      label: "Bank Name", required: true },
    ],
  },

  // ── Thailand ─────────────────────────────────────────────────────────────
  THA: {
    fields: [
      { key: "account_number", label: "Account Number", required: true },
      { key: "bank_name",      label: "Bank Name", required: true },
      { key: "account_type",   label: "Account Type", type: "select", options: ["Savings", "Current"], required: true },
    ],
  },

  // ── Kenya ────────────────────────────────────────────────────────────────
  KEN: {
    fields: [
      { key: "account_number", label: "Account Number", required: true },
      { key: "bank_name",      label: "Bank Name", required: true },
      { key: "branch_name",    label: "Branch Name", required: true },
    ],
  },

  // ── Ghana ────────────────────────────────────────────────────────────────
  GHA: {
    fields: [
      { key: "account_number", label: "Account Number", required: true },
      { key: "bank_name",      label: "Bank Name", required: true },
      { key: "branch_name",    label: "Branch Name", required: true },
    ],
  },
};

// Countries that use IBAN + BIC/SWIFT (alpha-3 codes to match backend)
const IBAN_COUNTRIES = new Set([
  "AND","AUT","AZE","BHR","BEL","BIH","BGR","CRI","HRV","CYP","CZE","DNK","DOM","EST",
  "FRO","FIN","FRA","GEO","DEU","GIB","GRC","GRL","GTM","HUN","ISL","IRL","ISR","ITA",
  "JOR","KAZ","KWT","LVA","LBN","LIE","LTU","LUX","MKD","MLT","MRT","MUS","MCO","MDA",
  "MNE","NLD","NOR","PAK","PSE","POL","PRT","QAT","ROU","SMR","SAU","SRB","SVK","SVN",
  "ESP","SWE","CHE","TUN","TUR","ARE","VAT","VGB","EGY","MAR","DZA","MAC",
]);

const IBAN_SCHEMA = {
  fields: [
    { key: "iban",  label: "IBAN",      placeholder: "e.g. DE89 3704 0044 0532 0130 00", required: true },
    { key: "bic",   label: "BIC / SWIFT", placeholder: "8 or 11 chars", required: true },
  ],
};

const GENERIC_SCHEMA = {
  fields: [
    { key: "account_number", label: "Account Number", required: true },
    { key: "swift",          label: "SWIFT / BIC Code", required: true },
    { key: "bank_name",      label: "Bank Name", required: true },
    { key: "bank_address",   label: "Bank Address", required: false },
  ],
};

const BANK_DETAIL_FIELDS = [
  { key: "bank_name",    label: "Bank Name", required: true },
  { key: "bank_address", label: "Bank Address", required: true },
  { key: "bank_zip",     label: "Bank ZIP / Postal Code", required: true },
];

const BANK_DETAIL_FIELD_KEYS = new Set(BANK_DETAIL_FIELDS.map((field) => field.key));

function getBankSchema(isoCode) {
  if (!isoCode) return null;
  if (SCHEMAS[isoCode]) return SCHEMAS[isoCode];
  if (IBAN_COUNTRIES.has(isoCode)) return IBAN_SCHEMA;
  return GENERIC_SCHEMA;
}

// ---------------------------------------------------------------------------

const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
const asArray = (value) => {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.data)) return value.data;
  if (Array.isArray(value?.items)) return value.items;
  if (Array.isArray(value?.results)) return value.results;
  return [];
};

export default function PaymentPage() {
  const { t } = useTranslation();
  const [method, setMethod] = useState("");
  const [details, setDetails] = useState({});
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [countries, setCountries] = useState([]);
  const [cities, setCities] = useState([]);
  const [loadingCities, setLoadingCities] = useState(false);

  useEffect(() => {
    fetchPayment();
    fetchCountries();
  }, []);

  // When loaded payment has a country_iso, fetch its cities
  useEffect(() => {
    if (details.country_iso) fetchCities(details.country_iso);
  }, [details.country_iso]);

  const fetchCountries = async () => {
    try {
      const { data } = await nestApi.get("/general/country");
      // Backend returns [{ code, name }] with code = ISO alpha-3 (e.g. "GBR")
      setCountries(asArray(data));
    } catch {
      toast.error("Failed to load countries");
    }
  };

  const fetchCities = async (countryCode) => {
    setLoadingCities(true);
    try {
      const { data } = await nestApi.get(`/general/city/${countryCode}`);
      // Backend returns [{ id, name, state }]
      setCities(asArray(data));
    } catch {
      setCities([]);
    } finally {
      setLoadingCities(false);
    }
  };

  const fetchPayment = async () => {
    try {
      const { data } = await nestApi.get("/rahe-portal/payment");
      setMethod(data.method || "");
      setDetails(data.details || {});
    } catch {
      toast.error("Failed to load payment details");
    }
  };

  const COUNTRY_OPTIONS = useMemo(
    () => countries.map((c) => ({ value: c.code, label: c.name })),
    [countries],
  );

  const CITY_OPTIONS = useMemo(
    () => cities.map((c) => ({
      value: String(c.id),
      label: c.state ? `${c.name}, ${c.state}` : c.name,
    })),
    [cities],
  );

  const selectedCountry = countries.find((c) => c.code === details.country_iso);
  const bankSchema = useMemo(() => getBankSchema(details.country_iso), [details.country_iso]);

  const validateForm = () => {
    const newErrors = {};

    if (method === "paypal") {
      if (!details.paypal_email) newErrors.paypal_email = "PayPal email is required";
      else if (!isValidEmail(details.paypal_email)) newErrors.paypal_email = "Enter a valid email";
    }

    if (method === "bank_transfer") {
      if (!details.country_iso) newErrors.country_iso = "Country is required";
      if (!details.account_holder_name?.trim()) newErrors.account_holder_name = "Account holder name is required";
      BANK_DETAIL_FIELDS.forEach((field) => {
        if (field.required && !details[field.key]?.trim()) {
          newErrors[field.key] = `${field.label} is required`;
        }
      });

      if (bankSchema) {
        bankSchema.fields.filter((f) => f.required).forEach((f) => {
          if (!details[f.key]?.trim()) newErrors[f.key] = `${f.label} is required`;
        });
      }
    }

    if (method === "revolut") {
      if (!details.full_name?.trim()) newErrors.full_name = "Full name is required";
      if (!details.revolut_tag?.trim()) newErrors.revolut_tag = "Revolut tag is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) return;
    setSaving(true);
    try {
      await nestApi.put("/rahe-portal/payment", { method, details });
      toast.success("Payment method saved as default");
    } catch {
      toast.error("Failed to update payment details");
    } finally {
      setSaving(false);
    }
  };

  const updateDetail = (key, value) => {
    setDetails((d) => ({ ...d, [key]: value }));
    if (errors[key]) setErrors((e) => ({ ...e, [key]: null }));
  };

  const handleCountryChange = (iso) => {
    // Reset country-specific fields but keep shared bank identity/address fields.
    setDetails((d) => ({
      account_holder_name: d.account_holder_name,
      bank_name: d.bank_name,
      bank_address: d.bank_address,
      bank_zip: d.bank_zip,
      country_iso: iso,
    }));
    setErrors({});
    setCities([]);
    if (iso) fetchCities(iso);
  };

  const handleMethodChange = (newMethod) => {
    setMethod(newMethod);
    setDetails({});
    setErrors({});
  };

  return (
    <div className="animate-slideUp space-y-6 max-w-4xl" data-testid="payment-page">
      <p className="text-sm text-slate-500">{t("payment.description")}</p>

      <div className="bg-white border border-slate-100 rounded-xl shadow-sm p-6 space-y-6">
        {/* Payment Method */}
        <div>
          <Label>{t("payment.selectMethod")} *</Label>
          <p className="text-xs text-slate-400 mb-2">Only one payment method can be active at a time</p>
          <Select value={method} onValueChange={handleMethodChange}>
            <SelectTrigger className="mt-1.5" data-testid="payment-method-select">
              <SelectValue placeholder={t("payment.selectMethod")} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="bank_transfer">{t("payment.bankTransfer")}</SelectItem>
              <SelectItem value="paypal">{t("payment.paypal")}</SelectItem>
              <SelectItem value="revolut">{t("payment.revolut")}</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* ── Bank Transfer ─────────────────────────────────────────────── */}
        {method === "bank_transfer" && (
          <div className="space-y-4 animate-fadeIn">
            {/* Country selector */}
            <div>
              <Label className={errors.country_iso ? "text-red-500" : ""}>{t("payment.country")} *</Label>
              <SearchableSelect
                options={COUNTRY_OPTIONS}
                value={details.country_iso || ""}
                onChange={handleCountryChange}
                placeholder="Select country…"
                searchPlaceholder="Search country…"
                className={`mt-1.5 ${errors.country_iso ? "border-red-400" : ""}`}
                testId="bank-country"
              />
              {errors.country_iso && <p className="text-xs text-red-500 mt-1">{errors.country_iso}</p>}
            </div>

            {/* City (cascaded from country) */}
            {details.country_iso && (
              <div>
                <Label>{t("payment.bankCity") || "City"}</Label>
                <SearchableSelect
                  options={CITY_OPTIONS}
                  value={details.city_id ? String(details.city_id) : ""}
                  onChange={(v) => {
                    const city = cities.find((c) => String(c.id) === String(v));
                    setDetails((d) => ({
                      ...d,
                      city_id: v,
                      city_name: city ? city.name : "",
                    }));
                  }}
                  placeholder={loadingCities ? "Loading cities…" : "Select city…"}
                  searchPlaceholder="Search city…"
                  className="mt-1.5"
                  testId="bank-city"
                />
              </div>
            )}

            {/* Account Holder Name + country-specific fields — key forces remount on country change */}
            {details.country_iso && (<React.Fragment key={details.country_iso}>
                <div>
                  <Label className={errors.account_holder_name ? "text-red-500" : ""}>Account Holder Name *</Label>
                  <Input
                    value={details.account_holder_name || ""}
                    onChange={(e) => updateDetail("account_holder_name", e.target.value)}
                    className={`mt-1.5 ${errors.account_holder_name ? "border-red-400" : ""}`}
                    placeholder="As it appears on your bank account"
                    data-testid="bank-holder-name"
                  />
                  {errors.account_holder_name && <p className="text-xs text-red-500 mt-1">{errors.account_holder_name}</p>}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
                  {BANK_DETAIL_FIELDS.map((field) => (
                    <div key={field.key}>
                      <Label className={errors[field.key] ? "text-red-500" : ""}>
                        {field.label}{field.required ? " *" : ""}
                      </Label>
                      <Input
                        value={details[field.key] || ""}
                        onChange={(e) => updateDetail(field.key, e.target.value)}
                        className={`mt-1.5 ${errors[field.key] ? "border-red-400" : ""}`}
                        data-testid={`bank-${field.key}`}
                      />
                      {errors[field.key] && <p className="text-xs text-red-500 mt-1">{errors[field.key]}</p>}
                    </div>
                  ))}
                </div>

                {/* Country-specific banking fields */}
                {bankSchema && (
                  <div key={details.country_iso} className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
                    {bankSchema.fields.filter((field) => !BANK_DETAIL_FIELD_KEYS.has(field.key)).map((field) =>
                      field.type === "select" ? (
                        <div key={field.key}>
                          <Label className={errors[field.key] ? "text-red-500" : ""}>
                            {field.label}{field.required ? " *" : ""}
                          </Label>
                          <Select
                            value={details[field.key] || ""}
                            onValueChange={(v) => updateDetail(field.key, v)}
                          >
                            <SelectTrigger className={`mt-1.5 ${errors[field.key] ? "border-red-400" : ""}`}>
                              <SelectValue placeholder={`Select ${field.label}`} />
                            </SelectTrigger>
                            <SelectContent>
                              {field.options.map((opt) => (
                                <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          {errors[field.key] && <p className="text-xs text-red-500 mt-1">{errors[field.key]}</p>}
                        </div>
                      ) : (
                        <div key={field.key}>
                          <Label className={errors[field.key] ? "text-red-500" : ""}>
                            {field.label}{field.required ? " *" : ""}
                          </Label>
                          <Input
                            value={details[field.key] || ""}
                            onChange={(e) => updateDetail(field.key, e.target.value)}
                            placeholder={field.placeholder || ""}
                            className={`mt-1.5 ${errors[field.key] ? "border-red-400" : ""}`}
                            data-testid={`bank-${field.key}`}
                          />
                          {errors[field.key] && <p className="text-xs text-red-500 mt-1">{errors[field.key]}</p>}
                        </div>
                      )
                    )}
                  </div>
                )}

                {/* Schema hint */}
                <p className="text-xs text-slate-400">
                  {selectedCountry && `Fields shown for ${selectedCountry.name} banking system.`}
                </p>
              </React.Fragment>
            )}
          </div>
        )}

        {/* ── PayPal ────────────────────────────────────────────────────── */}
        {method === "paypal" && (
          <div className="space-y-4 animate-fadeIn">
            <div>
              <Label className={errors.paypal_email ? "text-red-500" : ""}>{t("payment.paypalEmail")} *</Label>
              <Input
                type="email"
                value={details.paypal_email || ""}
                onChange={(e) => updateDetail("paypal_email", e.target.value)}
                className={`mt-1.5 ${errors.paypal_email ? "border-red-500" : ""}`}
                data-testid="paypal-email"
                placeholder="your@email.com"
              />
              {errors.paypal_email && <p className="text-xs text-red-500 mt-1">{errors.paypal_email}</p>}
            </div>
            <div>
              <Label>{t("payment.accountName")} *</Label>
              <Input
                value={details.account_name || ""}
                onChange={(e) => updateDetail("account_name", e.target.value)}
                className="mt-1.5"
                data-testid="paypal-name"
              />
            </div>
          </div>
        )}

        {/* ── Revolut ───────────────────────────────────────────────────── */}
        {method === "revolut" && (
          <div className="space-y-4 animate-fadeIn">
            <div>
              <Label className={errors.full_name ? "text-red-500" : ""}>{t("payment.fullName")} *</Label>
              <Input
                value={details.full_name || ""}
                onChange={(e) => updateDetail("full_name", e.target.value)}
                className={`mt-1.5 ${errors.full_name ? "border-red-400" : ""}`}
                data-testid="revolut-name"
              />
              {errors.full_name && <p className="text-xs text-red-500 mt-1">{errors.full_name}</p>}
            </div>
            <div>
              <Label className={errors.revolut_tag ? "text-red-500" : ""}>{t("payment.revolutTag")} *</Label>
              <Input
                value={details.revolut_tag || ""}
                onChange={(e) => updateDetail("revolut_tag", e.target.value)}
                placeholder="@your-revolut-tag"
                className={`mt-1.5 ${errors.revolut_tag ? "border-red-400" : ""}`}
                data-testid="revolut-tag"
              />
              {errors.revolut_tag && <p className="text-xs text-red-500 mt-1">{errors.revolut_tag}</p>}
            </div>
          </div>
        )}

        <Button
          onClick={handleSave}
          disabled={saving || !method}
          className="bg-[#1A1B2E] hover:bg-[#2D2E45] text-white px-8"
          data-testid="save-payment-btn"
        >
          {saving ? t("payment.saving") : "Save as Default"}
        </Button>
      </div>
    </div>
  );
}
